"use client";

import { Radar, Heart, Compass, Network, Send, Activity, type LucideIcon } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { processStages as stages } from "@/lib/data/platform-how-it-works";

const iconMap: Record<string, LucideIcon> = {
  Radar, Heart, Compass, Network, Send, Activity,
};

export function Process() {
  return (
    <Section id="process" className="relative">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-1/4 right-1/3 h-80 w-80 rounded-full bg-[var(--color-cyan)] opacity-15 blur-[120px]" />
      </div>

      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-cyan)]">The Six-Stage Process</span>
            <h2 className="mt-3">From Intelligence to Outcome — In Hours, Not Weeks</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">
              Each stage of the Perception X2 process is a discrete operation with defined inputs, engine ownership,
              processing methodology, and measurable outputs.
            </p>
          </div>
        </Reveal>

        <div className="mt-12 space-y-6">
          {stages.map((stage, i) => {
            const Icon = iconMap[stage.icon] ?? Radar;
            return (
              <Reveal key={stage.number} delay={i * 60}>
                <div
                  className="glass rounded-2xl p-7 sm:p-8"
                  style={{ boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${stage.accent}) 14%, transparent)` }}
                >
                  <div className="grid lg:grid-cols-[auto_1fr_1.2fr] gap-6 lg:gap-8 items-start">
                    <div className="flex flex-col items-start gap-3">
                      <span
                        className="grid place-items-center h-14 w-14 rounded-xl"
                        style={{
                          background: `color-mix(in oklch, var(--color-${stage.accent}) 18%, transparent)`,
                          boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${stage.accent}) 35%, transparent)`,
                        }}
                      >
                        <Icon className="h-6 w-6" style={{ color: `var(--color-${stage.accent})` }} />
                      </span>
                      <div>
                        <div className="eyebrow-tiny font-bold" style={{ color: `var(--color-${stage.accent})` }}>
                          Stage {stage.number}
                        </div>
                        <div className="text-base mt-1">{stage.name}</div>
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-widest text-[rgb(var(--fg-subtle))]">Engine</div>
                      <div className="text-sm font-semibold mt-1">{stage.engine}</div>
                      <div className="text-[10px] font-bold uppercase tracking-widest text-[rgb(var(--fg-subtle))] mt-4">Duration</div>
                      <div className="text-sm font-semibold mt-1">{stage.duration}</div>
                      <p className="mt-4 text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{stage.intro}</p>
                    </div>
                    <div>
                      <p className="text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{stage.body}</p>
                      <div className="mt-4">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-[rgb(var(--fg-subtle))]">Key Outputs</div>
                        <ul className="mt-3 space-y-2">
                          {stage.outputs.map((o) => (
                            <li key={o} className="flex items-start gap-2 text-sm text-[rgb(var(--fg-muted))]">
                              <span
                                className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0"
                                style={{ background: `var(--color-${stage.accent})` }}
                              />
                              <span>{o}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </Container>
    </Section>
  );
}
